import { Configuration, LogLevel, RedirectRequest, SilentRequest } from '@azure/msal-browser';

// Runtime environment configuration interface
interface RuntimeEnv {
  VITE_AZURE_CLIENT_ID?: string;
  VITE_AZURE_TENANT_ID?: string;
  VITE_REDIRECT_URI?: string;
  // Redirect URI used ONLY by silent (hidden-iframe) token renewal — the
  // MSAL redirect-bridge page. Empty/unset => <origin>/redirect.html.
  VITE_SILENT_REDIRECT_URI?: string;
  VITE_POST_LOGOUT_URI?: string;
  VITE_API_SCOPE?: string;
  VITE_API_BASE_URL?: string;
  VITE_AUTH_ADMIN_GROUP_ID?: string;
  VITE_AUTH_COMPANY_GROUPS?: string;
  VITE_TURNSTILE_SITE_KEY?: string;
  VITE_APP_VERSION?: string;
  VITE_BUILD_HASH?: string;
}

declare global {
  interface Window {
    ENV?: RuntimeEnv;
  }
}

// Helper to get env value: runtime (window.ENV) > build-time (import.meta.env) > default
function getEnv(key: keyof RuntimeEnv, defaultValue = ''): string {
  // Runtime config from container (production)
  if (window.ENV?.[key] && !window.ENV[key]?.startsWith('__')) {
    return window.ENV[key]!;
  }
  // Build-time config from Vite (local development)
  const viteValue = import.meta.env[key];
  if (viteValue) {
    return viteValue;
  }
  return defaultValue;
}

// MSAL configuration for Azure Entra ID authentication
const msalConfig: Configuration = {
  auth: {
    clientId: getEnv('VITE_AZURE_CLIENT_ID'),
    authority: `https://login.microsoftonline.com/${getEnv('VITE_AZURE_TENANT_ID', 'common')}`,
    redirectUri: getEnv('VITE_REDIRECT_URI', 'http://localhost:3000'),
    postLogoutRedirectUri: getEnv('VITE_POST_LOGOUT_URI', 'http://localhost:3000'),
    // MSAL v5: navigateToLoginRequestUrl was removed from BrowserAuthOptions.
    // It is now an option of handleRedirectPromise() (default: true), which
    // MsalProvider invokes internally.
  },
  cache: {
    cacheLocation: 'sessionStorage',
    // MSAL v5: storeAuthStateInCookie was removed from CacheOptions.
  },
  system: {
    // 2026-09-07: MSAL v5 silent renewal is BroadcastChannel-bridge only —
    // the hidden iframe must load /redirect.html (see silentApiRequest) and
    // answer via broadcastResponseToMainFrame(). If nothing answers within
    // this window MSAL throws BrowserAuthError "timed_out"
    // (subError redirect_bridge_timeout). Pinned explicitly (MSAL's own
    // default is 10 s) — a cold iframe on a slow connection needs the extra
    // margin, and an explicit number documents that this timeout is the
    // failure the interactive fallback in useAuth.ts recovers from.
    iframeBridgeTimeout: 12000,
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
          case LogLevel.Info:
            console.info(message);
            break;
          case LogLevel.Verbose:
            console.debug(message);
            break;
        }
      },
      logLevel: import.meta.env.DEV ? LogLevel.Info : LogLevel.Warning,
    },
  },
};

// Scopes for login request - includes custom API scope
export const loginRequest: RedirectRequest = {
  scopes: [
    'openid',
    'profile',
    'email',
    // Custom API scope - format: api://<backend-client-id>/<scope-name>
    getEnv('VITE_API_SCOPE', 'api://invoice-processor-api/access_as_user'),
  ],
};

// Scopes for acquiring access token for backend API
export const apiRequest: RedirectRequest = {
  scopes: [getEnv('VITE_API_SCOPE', 'api://invoice-processor-api/access_as_user')],
};

/**
 * Redirect URI for SILENT renewal only (2026-09-07).
 *
 * MSAL v5 renews tokens in a hidden iframe whose ONLY way of answering the
 * main frame is `broadcastResponseToMainFrame()` from
 * `@azure/msal-browser/redirect-bridge` — i.e. the page at the redirect URI
 * must be our tiny /redirect.html bridge. Until now silent requests inherited
 * auth.redirectUri (the SPA root), so the iframe loaded the whole app, never
 * broadcast anything, and every renewal died with `timed_out` after the
 * bridge timeout.
 *
 * auth.redirectUri deliberately stays the SPA root: INTERACTIVE logins keep
 * landing on the app itself (the bridge page would just bounce them).
 *
 * NOTE: this URI must ALSO be registered as an SPA redirect URI in the Entra
 * app registration (https://fv.fssrke3.com/redirect.html,
 * http://localhost:3000/redirect.html) — otherwise AAD answers AADSTS50011
 * inside the iframe and renewal still times out (the interactive fallback in
 * useAuth.ts keeps users working in that case).
 */
const bridgeUri =
  getEnv('VITE_SILENT_REDIRECT_URI') || `${window.location.origin}/redirect.html`;

export const silentApiRequest: SilentRequest = {
  scopes: apiRequest.scopes,
  redirectUri: bridgeUri,
};

// Backend API base URL
export const apiConfig = {
  baseUrl: getEnv('VITE_API_BASE_URL', '/api'),
};

// Cloudflare Turnstile (empty site key = widget disabled)
export const turnstileConfig = {
  siteKey: getEnv('VITE_TURNSTILE_SITE_KEY', ''),
};

// Group Object IDs for authorization (GUIDs from Entra ID)
export const authGroups = {
  // Admin group Object ID - users in this group have full access
  adminGroupId: getEnv('VITE_AUTH_ADMIN_GROUP_ID'),
  // Company group Object IDs - map of NIP to group Object ID
  // Format in env: VITE_AUTH_COMPANY_GROUPS=9372437672:guid1,5472132272:guid2
  companyGroups: parseCompanyGroups(getEnv('VITE_AUTH_COMPANY_GROUPS')),
};

// Parse company groups from env string "nip1:guid1,nip2:guid2"
function parseCompanyGroups(envString: string): Map<string, string> {
  const map = new Map<string, string>();
  if (!envString) return map;

  envString.split(',').forEach(pair => {
    const [nip, groupId] = pair.split(':');
    if (nip && groupId) {
      map.set(groupId.trim(), nip.trim()); // Map groupId -> NIP for reverse lookup
    }
  });
  return map;
}

export default msalConfig;
